/**
 * How long something has waited: `38 s`, `12 daq`, `3 soat`, `2 kun`.
 *
 * One unit, rounded down: what has waited 119 seconds has waited one minute.
 * Where the minutes matter past the hour (a visit that has run past its
 * norm), two: `3 soat 12 daq`, `2 kun 5 soat`.
 */
import type { Language } from "@/shared/i18n/languages";

import { NBSP } from "./money";

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

interface Units {
  second: string;
  minute: string;
  hour: string;
  day: string;
}

const UNITS: Readonly<Record<Language, Units>> = {
  uz: { second: "s", minute: "daq", hour: "soat", day: "kun" },
  ru: { second: "с", minute: "мин", hour: "ч", day: "дн" },
  en: { second: "s", minute: "min", hour: "h", day: "d" },
};

/**
 * A span in milliseconds as an age. A negative span (a clock that is ahead)
 * is zero. `exact` says the unit under the largest too, where there is some
 * of it: the minutes past the hour, the hours past the day.
 */
export function formatAge(milliseconds: number, language: Language, exact = false): string {
  const span = Number.isFinite(milliseconds) ? Math.max(0, milliseconds) : 0;
  const units = UNITS[language];
  if (span < MINUTE) return `${Math.floor(span / SECOND)}${NBSP}${units.second}`;
  if (span < HOUR) return `${Math.floor(span / MINUTE)}${NBSP}${units.minute}`;
  if (span < DAY) {
    const hours = `${Math.floor(span / HOUR)}${NBSP}${units.hour}`;
    const minutes = Math.floor((span % HOUR) / MINUTE);
    return exact && minutes > 0 ? `${hours} ${minutes}${NBSP}${units.minute}` : hours;
  }
  const days = `${Math.floor(span / DAY)}${NBSP}${units.day}`;
  const hours = Math.floor((span % DAY) / HOUR);
  return exact && hours > 0 ? `${days} ${hours}${NBSP}${units.hour}` : days;
}

/** Milliseconds from `since` to `now`; zero when `since` is not a moment. */
export function ageOf(since: string | Date, now: Date = new Date()): number {
  const from = typeof since === "string" ? new Date(since) : since;
  const span = now.getTime() - from.getTime();
  return Number.isFinite(span) ? Math.max(0, span) : 0;
}

/**
 * How long until the text of an age changes: a second while it counts
 * seconds, then to the next whole minute, hour or day. A clock that shows
 * `12 daq` has nothing to do for most of a minute.
 */
export function untilAgeChanges(milliseconds: number, exact = false): number {
  const span = Math.max(0, milliseconds);
  const step =
    span < MINUTE
      ? SECOND
      : span < HOUR
        ? MINUTE
        : span < DAY
          ? exact
            ? MINUTE
            : HOUR
          : exact
            ? HOUR
            : DAY;
  return step - (span % step);
}

/**
 * A span of seconds said exactly, every unit there is in it: `50 s`,
 * `1 daq 30 s`, `1 soat 30 daq`, `2 kun`. For what is set, not what has
 * waited: nothing is rounded away.
 */
export function formatDuration(seconds: number, language: Language): string {
  const units = UNITS[language];
  const whole = Number.isFinite(seconds) ? Math.max(0, Math.round(seconds)) : 0;
  const parts: [number, string][] = [
    [Math.floor(whole / 86_400), units.day],
    [Math.floor((whole % 86_400) / 3_600), units.hour],
    [Math.floor((whole % 3_600) / 60), units.minute],
    [whole % 60, units.second],
  ];
  const said = parts.filter(([count]) => count > 0);
  if (said.length === 0) return `0${NBSP}${units.second}`;
  return said.map(([count, unit]) => `${String(count)}${NBSP}${unit}`).join(" ");
}
